import { useEffect, useState, useRef, useCallback, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTemplateStore, type TemplateRecommendation } from '../store/template';
import { useSettingsStore } from '../store/settings';
import { useChatStore } from '../store/chat';
import { useAuth } from '../providers/AuthProvider';
import { getImportableWorkflow, type N8nTemplateNode } from '../lib/n8n-template-api';
import { NodeIcon } from '../components/NodeIcon';
import { FA_ICON_MAP } from '../utils/nodeIcons';
import { WorkflowCanvas } from '../components/workflow/WorkflowCanvas';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import type { Workflow } from '@n8n-builder/n8n-types';

// --- Icon Components ---

function MagnifyingGlassIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
    </svg>
  );
}

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function CheckBadgeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M16.403 12.652a3 3 0 000-5.304 3 3 0 00-3.75-3.751 3 3 0 00-5.305 0 3 3 0 00-3.751 3.75 3 3 0 000 5.305 3 3 0 003.75 3.751 3 3 0 005.305 0 3 3 0 003.751-3.75zm-2.546-4.46a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
    </svg>
  );
}

function ArrowTopRightOnSquareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
    </svg>
  );
}

function CubeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 7.5l-9-5.25L3 7.5m18 0l-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
    </svg>
  );
}

// --- Helpers ---

function stripMarkdown(text: string): string {
  return text
    .replace(/[#*_~`>\[\]()!]/g, '')
    .replace(/\n+/g, ' ')
    .trim();
}

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

function TemplateNodeIcon({ node, size = 24 }: { node: N8nTemplateNode; size?: number }) {
  // Use fileBuffer directly — the n8n API returns a complete data URI
  if (node.iconData?.type === 'file' && node.iconData.fileBuffer) {
    return (
      <div
        className="flex items-center justify-center rounded-md bg-gray-50 p-0.5"
        style={{ width: size, height: size }}
        title={node.displayName}
      >
        <img
          src={node.iconData.fileBuffer}
          alt={node.displayName}
          className="h-full w-full object-contain"
        />
      </div>
    );
  }

  // Font Awesome icons (iconData.type === 'icon', fileBuffer is the FA name like 'robot')
  if (node.iconData?.type === 'icon' && node.iconData.fileBuffer) {
    const IconComponent = FA_ICON_MAP[node.iconData.fileBuffer];
    if (IconComponent) {
      const iconSize = Math.round(size * 0.6);
      return (
        <div
          className="flex items-center justify-center rounded-md bg-gray-100"
          style={{ width: size, height: size }}
          title={node.displayName}
        >
          {createElement(IconComponent, {
            style: { width: iconSize, height: iconSize, color: '#6b7280' },
          })}
        </div>
      );
    }
  }

  // Fall back to NodeIcon which resolves via n8n-schema (Simple Icons CDN, n8n SVGs, FA, letter)
  return <NodeIcon nodeType={node.name} size={size} />;
}

// Reuse TemplateNodeIcon for AI recommendation nodes (same shape with optional iconData)
function RecommendationNodeIcon({ node, size = 24 }: { node: TemplateRecommendation['nodes'][number]; size?: number }) {
  if (node.iconData?.type === 'file' && node.iconData.fileBuffer) {
    return (
      <div
        className="flex items-center justify-center rounded-md bg-gray-50 p-0.5"
        style={{ width: size, height: size }}
        title={node.displayName}
      >
        <img src={node.iconData.fileBuffer} alt={node.displayName} className="h-full w-full object-contain" />
      </div>
    );
  }
  if (node.iconData?.type === 'icon' && node.iconData.fileBuffer) {
    const IconComponent = FA_ICON_MAP[node.iconData.fileBuffer];
    if (IconComponent) {
      const iconSize = Math.round(size * 0.6);
      return (
        <div className="flex items-center justify-center rounded-md bg-gray-100" style={{ width: size, height: size }} title={node.displayName}>
          {createElement(IconComponent, { style: { width: iconSize, height: iconSize, color: '#6b7280' } })}
        </div>
      );
    }
  }
  return <NodeIcon nodeType={node.name} size={size} />;
}

// --- AI Recommendation Card ---

function AIRecommendationCard({
  rec,
  rank,
  onUseTemplate,
  onViewDetails,
  isImporting,
}: {
  rec: TemplateRecommendation;
  rank: number;
  onUseTemplate: (id: number, name: string) => void;
  onViewDetails: (id: number) => void;
  isImporting: boolean;
}) {
  return (
    <div className="group relative flex flex-col rounded-xl border border-gray-200 bg-white p-5 transition-all duration-200 hover:border-gray-300 hover:shadow-sm">
      {/* Header: rank + node icons */}
      <div className="flex items-center gap-2.5 mb-3">
        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-500">
          {rank}
        </span>
        {rec.nodes.length > 0 && (
          <div className="flex items-center -space-x-1">
            {rec.nodes.slice(0, 5).map((node, i) => (
              <div key={i} className="rounded-md ring-2 ring-white">
                <RecommendationNodeIcon node={node} size={22} />
              </div>
            ))}
            {rec.nodes.length > 5 && (
              <span className="text-xs text-gray-400 ml-2">+{rec.nodes.length - 5}</span>
            )}
          </div>
        )}
      </div>

      {/* Title */}
      <h4
        onClick={() => onViewDetails(rec.templateId)}
        className="text-sm font-semibold text-gray-900 cursor-pointer hover:text-primary-600 transition-colors line-clamp-2 mb-2"
      >
        {rec.name}
      </h4>

      {/* AI explanation */}
      <p className="text-sm text-gray-500 line-clamp-3 mb-2 flex-1">{rec.aiExplanation}</p>

      {/* Suggested modifications */}
      {rec.suggestedModifications && (
        <p className="text-xs text-gray-400 mb-3 line-clamp-2">
          <span className="font-medium text-gray-500">Tip:</span> {rec.suggestedModifications}
        </p>
      )}

      {/* Footer: meta + actions */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <EyeIcon className="h-3.5 w-3.5" />
            {formatNumber(rec.totalViews)}
          </span>
          <span>
            {rec.user.name}
            {rec.user.verified && <CheckBadgeIcon className="inline h-3.5 w-3.5 text-blue-500 ml-0.5" />}
          </span>
        </div>
        <button
          onClick={() => onUseTemplate(rec.templateId, rec.name)}
          disabled={isImporting}
          className="rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50"
        >
          Use Template
        </button>
      </div>
    </div>
  );
}

// --- Main Component ---

export function TemplatesPage() {
  const {
    searchQuery,
    selectedCategory,
    currentPage,
    rowsPerPage,
    templates,
    totalCount,
    categories,
    isLoading,
    isDetailLoading,
    selectedTemplate,
    error,
    setSearchQuery,
    setSelectedCategory,
    setCurrentPage,
    fetchTemplates,
    fetchCategories,
    openTemplateDetail,
    closeTemplateDetail,
    resetFilters,
    // AI search
    aiSearchActive,
    aiSearchLoading,
    aiSearchStatus,
    aiRecommendations,
    aiSearchError,
    startAISearch,
    clearAISearch,
  } = useTemplateStore();

  const { isAuthenticated } = useAuth();
  const { setChatMode } = useSettingsStore();
  const { startNewConversation, setAttachedWorkflow } = useChatStore();
  const navigate = useNavigate();

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [aiQuery, setAiQuery] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const categoriesRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to top when entering detail view
  useEffect(() => {
    if (selectedTemplate || isDetailLoading) {
      containerRef.current?.closest('main')?.scrollTo({ top: 0 });
    }
  }, [selectedTemplate, isDetailLoading]);

  // Fetch categories on mount
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  // Fetch templates when search/category/page changes
  useEffect(() => {
    fetchTemplates();
  }, [searchQuery, selectedCategory, currentPage, fetchTemplates]);

  // Debounce search input
  const handleSearchChange = useCallback(
    (value: string) => {
      setLocalSearch(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setSearchQuery(value);
      }, 300);
    },
    [setSearchQuery]
  );

  // Cleanup debounce timer
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // AI search submit
  const handleAISearch = () => {
    if (!aiQuery.trim() || !isAuthenticated) return;
    startAISearch(aiQuery.trim());
  };

  // "Use Template" action - matches WorkflowsPage pattern
  const handleUseTemplate = async (templateId: number, templateName: string) => {
    setIsImporting(true);
    try {
      const workflowJson = await getImportableWorkflow(templateId);
      const workflow = workflowJson as unknown as Workflow;
      const nodeCount = Array.isArray(workflow.nodes) ? workflow.nodes.length : 0;

      startNewConversation();
      setAttachedWorkflow({
        filename: templateName,
        workflow,
        nodeCount,
      });
      setChatMode('edit');
      closeTemplateDetail();
      navigate('/workflow-builder');
    } catch (err) {
      console.error('Failed to import template:', err);
    } finally {
      setIsImporting(false);
    }
  };

  // Pagination
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;

  // Top-level categories only (no parent)
  const topCategories = categories.filter((c) => !c.parent);

  return (
    <div ref={containerRef} className="min-h-full bg-gray-50 p-4 md:p-6">
      {/* Inline Detail View */}
      {(selectedTemplate || isDetailLoading) ? (
        <div className="max-w-5xl mx-auto animate-fade-in">
          {/* Back button */}
          <button
            onClick={closeTemplateDetail}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors mb-6"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Back to Templates
          </button>

          {isDetailLoading && !selectedTemplate ? (
            /* Loading skeleton */
            <div className="space-y-4">
              <div className="h-8 w-2/3 rounded-lg skeleton" />
              <div className="h-5 w-1/3 rounded skeleton" />
              <div className="h-[450px] rounded-xl skeleton" />
              <div className="h-10 w-48 rounded-lg skeleton" />
              <div className="h-24 rounded-lg skeleton" />
            </div>
          ) : selectedTemplate ? (
            <div className="space-y-6">
              {/* Title + meta */}
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {selectedTemplate.name}
                </h1>
                <div className="mt-2 flex items-center gap-3 text-sm text-gray-500">
                  <div className="flex items-center gap-1.5">
                    {selectedTemplate.user.avatar && (
                      <img
                        src={selectedTemplate.user.avatar}
                        alt={selectedTemplate.user.name}
                        className="h-5 w-5 rounded-full"
                      />
                    )}
                    <span>{selectedTemplate.user.name}</span>
                    {selectedTemplate.user.verified && (
                      <CheckBadgeIcon className="h-4 w-4 text-blue-500" />
                    )}
                  </div>
                  <span className="text-gray-300">&middot;</span>
                  <span className="flex items-center gap-1">
                    <EyeIcon className="h-4 w-4" />
                    {formatNumber(selectedTemplate.totalViews)} views
                  </span>
                </div>
              </div>

              {/* Workflow viewer */}
              {selectedTemplate.workflow && (
                <div className="rounded-xl border border-gray-200 overflow-hidden" style={{ height: 450 }}>
                  <WorkflowCanvas workflow={selectedTemplate.workflow as unknown as Workflow} />
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() =>
                    handleUseTemplate(selectedTemplate.id, selectedTemplate.name)
                  }
                  disabled={isImporting}
                  className="flex items-center gap-2 rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50"
                >
                  {isImporting ? (
                    <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Importing...
                    </>
                  ) : (
                    'Use Template'
                  )}
                </button>
                <a
                  href={`https://n8n.io/workflows/${selectedTemplate.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  View on n8n.io
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
              </div>

              {/* Description */}
              {selectedTemplate.description && (
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 mb-2">Description</h2>
                  <div className="prose prose-sm prose-gray max-w-none">
                    <ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
                      {selectedTemplate.description}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              {/* Nodes */}
              {selectedTemplate.nodes.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 mb-2">
                    Nodes ({selectedTemplate.nodes.length})
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.nodes.map((node, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5"
                      >
                        <TemplateNodeIcon node={node} size={20} />
                        <span className="text-sm text-gray-700">{node.displayName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Categories */}
              {selectedTemplate.categories.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold text-gray-900 mb-2">Categories</h2>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.categories.map((cat) => (
                      <span
                        key={cat.id}
                        className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600"
                      >
                        {cat.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          {/* AI Search */}
          <div className="mb-8 mt-4 max-w-2xl mx-auto">
            <div className="flex items-center gap-2 mb-3">
              <SparklesIcon className="h-5 w-5 text-primary-500" />
              <h2 className="text-lg font-semibold text-gray-900">What would you like to automate?</h2>
            </div>
            <div className="relative">
              <textarea
                value={aiQuery}
                onChange={(e) => setAiQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAISearch(); } }}
                placeholder={isAuthenticated ? 'e.g., Send a Slack message when a Google Sheets row is added' : 'Sign in to search with AI'}
                disabled={!isAuthenticated || aiSearchLoading}
                rows={2}
                className="w-full resize-none rounded-xl border border-gray-300 bg-white py-3 pl-4 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50 disabled:text-gray-400"
              />
              <button
                onClick={handleAISearch}
                disabled={!isAuthenticated || !aiQuery.trim() || aiSearchLoading}
                className="absolute right-2.5 bottom-2.5 flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {aiSearchLoading ? (
                  <>
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Searching
                  </>
                ) : (
                  <>
                    <SparklesIcon className="h-3.5 w-3.5" />
                    Search
                  </>
                )}
              </button>
            </div>
          </div>

          {/* AI Search Results */}
          {aiSearchActive && (
            <div className="mb-8">
              {/* Status / Loading */}
              {aiSearchLoading && (
                <div className="flex items-center gap-3 rounded-lg bg-gray-50 px-4 py-3 mb-4 max-w-2xl mx-auto">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-300 border-t-primary-600" />
                  <span className="text-sm text-gray-600">{aiSearchStatus || 'Searching...'}</span>
                </div>
              )}

              {/* Error */}
              {aiSearchError && (
                <div className="flex items-center justify-between rounded-lg border border-red-200 bg-red-50 px-4 py-3 mb-4 max-w-2xl mx-auto">
                  <p className="text-sm text-red-600">{aiSearchError}</p>
                  <button
                    onClick={clearAISearch}
                    className="text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {/* Recommendations */}
              {aiRecommendations.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-500">
                      {aiRecommendations.length} template{aiRecommendations.length !== 1 ? 's' : ''} recommended
                    </span>
                    <button
                      onClick={clearAISearch}
                      className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <XMarkIcon className="h-3.5 w-3.5" />
                      Clear
                    </button>
                  </div>
                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {aiRecommendations.slice(0, 3).map((rec, i) => (
                      <AIRecommendationCard
                        key={rec.templateId}
                        rec={rec}
                        rank={i + 1}
                        onUseTemplate={handleUseTemplate}
                        onViewDetails={openTemplateDetail}
                        isImporting={isImporting}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {!aiSearchLoading && !aiSearchError && aiRecommendations.length === 0 && (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50 py-8 max-w-2xl mx-auto">
                  <p className="text-sm text-gray-500">No matching templates found. Try a different description.</p>
                  <button
                    onClick={clearAISearch}
                    className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Header */}
          <header className="mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              <CubeIcon className="h-4 w-4" />
              <span>Browse</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Template Library</h1>
            <p className="mt-1 text-gray-500">
              Browse 8,400+ community n8n workflow templates
            </p>
          </header>

          {/* Search */}
          <div className="mb-4">
            <div className="relative max-w-xl">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search templates..."
                value={localSearch}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
              {localSearch && (
                <button
                  onClick={() => {
                    setLocalSearch('');
                    setSearchQuery('');
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Category pills */}
          {topCategories.length > 0 && (
            <div className="mb-6 relative">
              <div
                ref={categoriesRef}
                className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide"
              >
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`flex-shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                    !selectedCategory
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  All
                </button>
                {topCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() =>
                      setSelectedCategory(selectedCategory === cat.name ? null : cat.name)
                    }
                    className={`flex-shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                      selectedCategory === cat.name
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {cat.icon} {cat.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results count */}
          {!isLoading && totalCount > 0 && (
            <p className="mb-4 text-sm text-gray-500">
              {formatNumber(totalCount)} templates found
            </p>
          )}

          {/* Content */}
          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-52 rounded-xl skeleton" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-red-200 bg-red-50 p-12">
              <p className="text-sm text-red-600">{error}</p>
              <button
                onClick={fetchTemplates}
                className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Try again
              </button>
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 p-12">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gray-100">
                <MagnifyingGlassIcon className="h-7 w-7 text-gray-400" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-gray-900">No templates found</h3>
              <p className="mt-1 text-center text-gray-500">
                {searchQuery
                  ? `No templates matching "${searchQuery}"`
                  : 'No templates in this category'}
              </p>
              <button
                onClick={resetFilters}
                className="mt-4 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Clear filters
              </button>
            </div>
          ) : (
            <>
              {/* Template grid */}
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {templates.map((template) => (
                  <div
                    key={template.id}
                    onClick={() => openTemplateDetail(template.id)}
                    className="group relative rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition-all duration-200 hover:border-gray-300 hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
                  >
                    {/* Node icons row */}
                    <div className="flex items-center gap-1.5 mb-3">
                      {template.nodes.slice(0, 5).map((node, i) => (
                        <TemplateNodeIcon key={i} node={node} size={24} />
                      ))}
                      {template.nodes.length > 5 && (
                        <span className="text-xs text-gray-400 ml-1">
                          +{template.nodes.length - 5}
                        </span>
                      )}
                    </div>

                    {/* Template name */}
                    <h3 className="font-semibold text-gray-900 text-base mb-1 line-clamp-2">
                      {template.name}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                      {stripMarkdown(template.description)}
                    </p>

                    {/* Footer: creator + views */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 min-w-0">
                        {template.user.avatar && (
                          <img
                            src={template.user.avatar}
                            alt={template.user.name}
                            className="h-5 w-5 rounded-full flex-shrink-0"
                          />
                        )}
                        <span className="text-xs text-gray-500 truncate">
                          {template.user.name}
                        </span>
                        {template.user.verified && (
                          <CheckBadgeIcon className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" />
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0">
                        <EyeIcon className="h-3.5 w-3.5" />
                        {formatNumber(template.totalViews)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-8 flex items-center justify-center gap-4">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!hasPrev}
                    className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                    Previous
                  </button>
                  <span className="text-sm text-gray-500">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!hasNext}
                    className="flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
